import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { langFromUrl, readStoredLang, writeStoredLang, type Lang } from './lang';

// Lightweight EN/ES i18n for the public events area (capcana.app/events).
// English is the default. The choice persists in localStorage and can be
// pre-selected with ?lang=es / ?lang=en. Event content itself is localized via
// pickLocalized() against the *_en / *_es fields the backend returns.
// The mechanism (Lang, storage, placeholders) is shared with the admin: see ./lang.ts.

export type { Lang };

const STORAGE_KEY = 'publicEventsLang';

const readInitialLang = (): Lang => langFromUrl() ?? readStoredLang(STORAGE_KEY) ?? 'en';

// Pick the value for the active language from an object holding `${base}_en` /
// `${base}_es` fields, gracefully falling back to the other language, then to a
// non-suffixed field, then empty string.
export const pickLocalized = (
  obj: Record<string, any> | null | undefined,
  base: string,
  lang: Lang,
): string => {
  if (!obj) return '';
  const primary = obj[`${base}_${lang}`];
  if (primary != null && String(primary).trim() !== '') return String(primary);
  const fallbackLang: Lang = lang === 'en' ? 'es' : 'en';
  const secondary = obj[`${base}_${fallbackLang}`];
  if (secondary != null && String(secondary).trim() !== '') return String(secondary);
  const bare = obj[base];
  return bare != null ? String(bare) : '';
};

type Dict = Record<string, string>;

const STRINGS: Record<Lang, Dict> = {
  en: {
    searchByName: 'Search by name...',
    all: 'All',
    noEventsTitle: 'No events available',
    noEventsBody: 'There are no events available at this time',
    // Phone step
    phoneVerification: 'Phone Verification',
    enterYourPhone: 'Enter your phone number',
    phoneNumber: 'Phone Number',
    howReceiveCode: 'How would you like to receive the code?',
    agreeReceiveSms: 'I agree to receive SMS messages for verification purposes',
    agreeReceiveWhatsapp: 'I agree to receive WhatsApp messages for verification purposes',
    continue: 'Continue',
    sending: 'Sending...',
    // Pin step
    verifyCode: 'Verify Code',
    enterVerificationCode: 'Enter verification code',
    weSentCodeTo: 'We sent a code to',
    verifying: 'Verifying...',
    resendCode: 'Resend code',
    resendCodeIn: 'Resend code in',
    // Companions step
    companions: 'Companions',
    howManyAttend: 'How many people will attend?',
    adults: 'Adults',
    children: 'Children',
    // Guests step
    mainGuest: 'Main Guest',
    companion: 'Companion',
    scanYourId: 'Scan your ID document',
    scanIdFor: 'Scan ID for',
    registering: 'Registering...',
    confirmInvitation: 'Confirm Invitation',
    saveContinue: 'Save & Continue',
    // Detail
    details: 'Details',
    about: 'About',
    startAt: 'Start at:',
    endAt: 'End at:',
    register: 'Register',
    // Scan form
    identificationNumber: 'Identification Number',
    name: 'Name',
    lastName: 'Last Name',
    dateOfBirth: 'Date of Birth',
    nationality: 'Nationality',
    expirationDate: 'Expiration Date',
    upload: 'Upload',
    documentAnalyzed: 'Document analyzed',
    couldNotAnalyzeDocument: 'Could not analyze document',
    tapToScanAgain: 'Tap to scan again',
    // Success / wallet
    addToWallet: 'Add to Wallet',
    savePassToGallery: 'Save pass to gallery',
    invitationCancelled: 'Invitation cancelled',
    invitationCancelledHint: 'This invitation is no longer valid. Contact the person who invited you.',
    generatingPass: 'Generating...',
    copiedToClipboard: 'Copied to clipboard!',
    // Validation
    enterPhoneNumber: 'Please enter a phone number',
    phoneTooShort: 'Phone number is too short',
    phoneTooLong: 'Phone number is too long',
    acceptSmsConsent: 'You must accept the consent to continue',
    completeChallenge: 'Please complete the verification challenge',
    failedSendCode: 'Failed to send verification code',
    whatsappUnavailable: 'WhatsApp is not available right now, use SMS',
    enterFullCode: 'Please enter the full 4-digit code',
    invalidCode: 'Invalid verification code',
    atLeastOneGuest: 'At least one guest is required',
    completeMainGuest: 'Please complete all required fields for the main guest',
    nameRequired: 'Name is required',
    lastNameRequired: 'Last name is required',
    idRequired: 'Identification number is required',
    docAlreadyUsed: 'This document number is already used by another guest',
    nameExists: 'A guest with this name already exists',
    docAlreadyScanned: 'This document has already been scanned. Please use a different ID.',
    couldNotAnalyze: 'Could not analyze the document. Please fill in the information manually.',
    registrationFailed: 'Registration failed',
    searchCountry: 'Search country...',
    eventNotFound: 'Event not found',
    eventRemoved: 'This event may have been removed or the link is invalid.',
    viewAllEvents: 'View All Events',
    person: 'person',
    persons: 'persons',
    adultsLower: 'adults',
    childrenLower: 'children',
    share: 'Share',
    registrationComplete: 'Registration Complete',
    weWillFill: "We'll fill in the details automatically from your ID document.",
    takePhotoHint: 'Take a photo or select from gallery',
    // Private events (SAM-730)
    privateEvent: 'Private event',
    privateEventLock: 'Private event, code required to register',
    eventCode: 'Event code',
    privateEventEnterCode: 'This is a private event. Enter the event code to register.',
    enterEventCode: 'Please enter the event code',
    wrongEventCode: 'Wrong event code',
    eventCodeRejected: 'The event code was not accepted. Enter it again.',
    couldNotCheckCode: 'Could not check the code. Please try again.',
    // SAM-737: failed-attempt limit on the event code. {n} is replaced with the minutes.
    eventCodeLocked: 'Too many attempts. Try again in {n} minutes.',
    eventCodeUnavailable: 'The event code cannot be checked right now. Please try again later.',
  },
  es: {
    searchByName: 'Buscar por nombre...',
    all: 'Todos',
    noEventsTitle: 'No hay eventos disponibles',
    noEventsBody: 'No hay eventos disponibles en este momento',
    phoneVerification: 'Verificación de teléfono',
    enterYourPhone: 'Ingresa tu número de teléfono',
    phoneNumber: 'Número de teléfono',
    howReceiveCode: '¿Cómo deseas recibir el código?',
    agreeReceiveSms: 'Acepto recibir mensajes SMS con fines de verificación',
    agreeReceiveWhatsapp: 'Acepto recibir mensajes de WhatsApp con fines de verificación',
    continue: 'Continuar',
    sending: 'Enviando...',
    verifyCode: 'Verificar código',
    enterVerificationCode: 'Ingresa el código de verificación',
    weSentCodeTo: 'Enviamos un código a',
    verifying: 'Verificando...',
    resendCode: 'Reenviar código',
    resendCodeIn: 'Reenviar código en',
    companions: 'Acompañantes',
    howManyAttend: '¿Cuántas personas asistirán?',
    adults: 'Adultos',
    children: 'Niños',
    mainGuest: 'Invitado principal',
    companion: 'Acompañante',
    scanYourId: 'Escanea tu documento de identidad',
    scanIdFor: 'Escanea el documento de',
    registering: 'Registrando...',
    confirmInvitation: 'Confirmar invitación',
    saveContinue: 'Guardar y continuar',
    details: 'Detalles',
    about: 'Acerca de',
    startAt: 'Inicia:',
    endAt: 'Finaliza:',
    register: 'Registrarme',
    identificationNumber: 'Número de identificación',
    name: 'Nombre',
    lastName: 'Apellido',
    dateOfBirth: 'Fecha de nacimiento',
    nationality: 'Nacionalidad',
    expirationDate: 'Fecha de vencimiento',
    upload: 'Subir',
    documentAnalyzed: 'Documento analizado',
    couldNotAnalyzeDocument: 'No se pudo analizar el documento',
    tapToScanAgain: 'Toca para escanear de nuevo',
    addToWallet: 'Agregar a Wallet',
    savePassToGallery: 'Guardar pase en galería',
    invitationCancelled: 'Invitación cancelada',
    invitationCancelledHint: 'Esta invitación ya no es válida. Contacta a quien te invitó.',
    generatingPass: 'Generando...',
    copiedToClipboard: '¡Copiado al portapapeles!',
    enterPhoneNumber: 'Por favor ingresa un número de teléfono',
    phoneTooShort: 'El número de teléfono es muy corto',
    phoneTooLong: 'El número de teléfono es muy largo',
    acceptSmsConsent: 'Debes aceptar el consentimiento para continuar',
    completeChallenge: 'Por favor completa el desafío de verificación',
    failedSendCode: 'No se pudo enviar el código de verificación',
    whatsappUnavailable: 'WhatsApp no está disponible ahora, usa SMS',
    enterFullCode: 'Por favor ingresa el código completo de 4 dígitos',
    invalidCode: 'Código de verificación inválido',
    atLeastOneGuest: 'Se requiere al menos un invitado',
    completeMainGuest: 'Por favor completa todos los campos del invitado principal',
    nameRequired: 'El nombre es obligatorio',
    lastNameRequired: 'El apellido es obligatorio',
    idRequired: 'El número de identificación es obligatorio',
    docAlreadyUsed: 'Este número de documento ya está usado por otro invitado',
    nameExists: 'Ya existe un invitado con este nombre',
    docAlreadyScanned: 'Este documento ya fue escaneado. Usa una identificación diferente.',
    couldNotAnalyze: 'No se pudo analizar el documento. Completa la información manualmente.',
    registrationFailed: 'El registro falló',
    searchCountry: 'Buscar país...',
    eventNotFound: 'Evento no encontrado',
    eventRemoved: 'Este evento pudo haber sido eliminado o el enlace es inválido.',
    viewAllEvents: 'Ver todos los eventos',
    person: 'persona',
    persons: 'personas',
    adultsLower: 'adultos',
    childrenLower: 'niños',
    share: 'Compartir',
    registrationComplete: 'Registro completado',
    weWillFill: 'Completaremos los datos automáticamente desde tu documento de identidad.',
    takePhotoHint: 'Toma una foto o selecciona de la galería',
    privateEvent: 'Evento privado',
    privateEventLock: 'Evento privado, se requiere código para registrarse',
    eventCode: 'Código del evento',
    privateEventEnterCode: 'Este es un evento privado. Ingresa el código del evento para registrarte.',
    enterEventCode: 'Por favor ingresa el código del evento',
    wrongEventCode: 'Código del evento incorrecto',
    eventCodeRejected: 'El código del evento no fue aceptado. Ingrésalo de nuevo.',
    couldNotCheckCode: 'No se pudo verificar el código. Intenta de nuevo.',
    eventCodeLocked: 'Demasiados intentos. Intenta de nuevo en {n} minutos.',
    eventCodeUnavailable: 'No se puede verificar el código en este momento. Intenta más tarde.',
  },
};

interface PublicLangCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: keyof typeof STRINGS['en']) => string;
}

const Ctx = createContext<PublicLangCtx | null>(null);

export const PublicLangProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [lang, setLangState] = useState<Lang>(readInitialLang);

  const setLang = (l: Lang) => {
    setLangState(l);
    writeStoredLang(STORAGE_KEY, l);
  };

  // Keep <html lang> in sync for accessibility / SEO.
  useEffect(() => {
    try { document.documentElement.lang = lang; } catch { /* ignore */ }
  }, [lang]);

  const value = useMemo<PublicLangCtx>(
    () => ({ lang, setLang, t: (key) => STRINGS[lang][key] ?? STRINGS.en[key] ?? String(key) }),
    [lang],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

// Usable without a provider (falls back to localStorage/English) so components
// shared with non-events flows don't crash.
export const useLang = (): PublicLangCtx => {
  const ctx = useContext(Ctx);
  if (ctx) return ctx;
  const lang = readInitialLang();
  return { lang, setLang: () => {}, t: (key) => STRINGS[lang][key] ?? STRINGS.en[key] ?? String(key) };
};
